/* Rhody Reviews api/v1 -- integrations domain. PURE EXTRACTION from lib/api.ts (gate-prep split); no behavior change. */
import { request } from './_http';

// -------------------------------------------------------------------
// Integrations layer (connectors + universal intake + baseline import)
// -------------------------------------------------------------------
export interface IntegrationKeyInfo {
  key_prefix: string;
  masked: string;
  label: string | null;
  created_at: string | null;
  last_used_at: string | null;
}
export interface IntegrationKeyResponse {
  client_id: string;
  webhook_url: string;
  keys: IntegrationKeyInfo[];
  has_key: boolean;
  payload_example: unknown;
}
export interface IntegrationKeyCreated {
  client_id: string;
  webhook_url: string;
  integration_key: string; // PLAINTEXT, shown once
  key_prefix: string;
}
export interface ConnectorNeeds {
  provider: string;
  label: string;
  app_name: string;
  redirect_uri: string;
  scopes: string[];
  client_id_env: string;
  client_secret_env: string;
  /** Extra env vars beyond client id/secret needed to go live (e.g. a webhook Verifier Token). */
  extra_env_vars?: string[];
  register_at: string;
}
export interface ConnectorNeedsKey {
  provider: string;
  label: string;
  auth: 'api_key';
  /** Plain-language requirement (e.g. the Housecall Pro MAX plan). */
  requirement_note: string;
  /** Where the pro generates their API key. */
  key_help_url: string;
}
export interface ConnectorStatus {
  provider: string;
  label: string;
  description: string;
  /** 'oauth' = connect via redirect; 'api_key' = paste-your-key form (HouseCall Pro);
      'webhook' = guided copy-the-URL connect (Workiz/Sweep&Go/ResponsiBid). */
  auth?: 'oauth' | 'api_key' | 'webhook';
  /** 'api_key' (default) or 'webhook'. Drives which connect card the UI renders. */
  connect_kind?: 'api_key' | 'webhook';
  /** Numbered, owner-facing steps for a webhook connector: where to paste the URL. */
  steps?: string[];
  configured: boolean;
  /** true = instant self-service connector (Square/Calendly/Acuity); false = review-gated (Jobber/QuickBooks) or key-based (HouseCall Pro). */
  instant: boolean;
  /** true ONLY when the end-to-end data layer is actually built. Independent of `configured`. */
  data_layer_live: boolean;
  /** Customer-side requirement shown in the connect card (e.g. Calendly's paid-plan need). Empty string = none. */
  requirement_note?: string;
  needs: ConnectorNeeds | ConnectorNeedsKey | null;
  status: 'connected' | 'disconnected' | 'error';
  connected: boolean;
  account: Record<string, unknown> | null;
  connect_url: string | null;
  /** true when the UI should render a paste-your-key connect form (key connectors). */
  connect_form?: boolean;
  /** true once the per-account webhook signing secret is stored (key connectors). */
  webhook_secret_saved?: boolean;
  /** Per-tenant webhook URL to register with the vendor. Present for connected instant + key connectors. */
  webhook_url: string | null;
}
export interface ConnectKeyResult {
  ok: boolean;
  client_id: string;
  provider: string;
  company_id: string | null;
  webhook_url: string;
  next_step: string;
  webhook_secret_saved: boolean;
}
export interface SaveWebhookSecretResult {
  ok: boolean;
  client_id: string;
  provider: string;
  active: boolean;
}
export interface ConnectWebhookResult {
  ok: boolean;
  client_id: string;
  provider: string;
  webhook_url: string;
  steps: string[];
}
export interface ConnectorsResponse {
  client_id: string;
  connectors: ConnectorStatus[];
}
export interface BaselineImportPlatformResult {
  platform: string;
  imported: number;
  total_count: number | null;
  avg_rating: number | null;
  full_history: boolean;
  note: string;
}
export interface BaselineImportResult {
  client_id: string;
  results: BaselineImportPlatformResult[];
  total_imported: number;
}
export interface FindPlaceResult {
  ok: boolean;
  client_id: string;
  place_id: string;
  match: { name: string; address: string };
}

export const integrationsApi = {
  /* ----- Integrations: Zapier / generic webhook key ----- */
  getIntegrationKey(signal?: AbortSignal): Promise<IntegrationKeyResponse> {
    return request<IntegrationKeyResponse>('/api/v1/integrations/key', { signal });
  },
  createIntegrationKey(rotate = true): Promise<IntegrationKeyCreated> {
    return request<IntegrationKeyCreated>('/api/v1/integrations/key', {
      method: 'POST',
      body: { rotate },
    });
  },

  /* ----- Integrations: native connectors status / disconnect ----- */
  listConnectors(signal?: AbortSignal): Promise<ConnectorsResponse> {
    return request<ConnectorsResponse>('/api/v1/integrations/connectors', { signal });
  },
  disconnectConnector(provider: string): Promise<{ client_id: string; disconnected: string }> {
    return request<{ client_id: string; disconnected: string }>(
      `/api/v1/integrations/connectors/${encodeURIComponent(provider)}`,
      { method: 'DELETE' },
    );
  },

  /* ----- Key-based connector connect (HouseCall Pro): paste API key + secret -----
     HCP is API-key auth (not OAuth). The pro pastes their own Housecall Pro API
     key (requires the MAX plan); we validate + store it and hand back a per-tenant
     webhook URL. A second call saves the per-account webhook signing secret HCP
     returns when the pro registers that URL -- only then does the receiver fire. */
  connectConnectorKey(provider: string, apiKey: string): Promise<ConnectKeyResult> {
    return request<ConnectKeyResult>(
      `/api/v1/integrations/connectors/${encodeURIComponent(provider)}/connect-key`,
      { method: 'POST', body: { api_key: apiKey } },
    );
  },
  saveConnectorWebhookSecret(provider: string, webhookSecret: string): Promise<SaveWebhookSecretResult> {
    return request<SaveWebhookSecretResult>(
      `/api/v1/integrations/connectors/${encodeURIComponent(provider)}/webhook-secret`,
      { method: 'POST', body: { webhook_secret: webhookSecret } },
    );
  },

  /* ----- Guided WEBHOOK connector connect (Workiz / Sweep&Go / ResponsiBid) -----
     These vendors have no API key + no OAuth: the owner copies the per-tenant
     webhook URL we return into the vendor's webhook setting. "Connect" creates
     the connector row (so the receiver stops failing closed) and returns the URL
     + numbered paste-it-here steps. */
  connectConnectorWebhook(provider: string): Promise<ConnectWebhookResult> {
    return request<ConnectWebhookResult>(
      `/api/v1/integrations/connectors/${encodeURIComponent(provider)}/connect-webhook`,
      { method: 'POST', body: {} },
    );
  },

  /* ----- Integrations: existing-review baseline import ----- */
  importExistingReviews(platforms?: string[]): Promise<BaselineImportResult> {
    return request<BaselineImportResult>('/api/v1/integrations/import-reviews', {
      method: 'POST',
      body: platforms ? { platforms } : {},
    });
  },

  /* ----- Integrations: Google Places "find my business" fallback -----
     Resolves the owner's business name (+ optional address) to a Google
     place_id and stamps it on the client row, so review import works even
     when the GBP API is not approved. */
  findMyBusiness(name: string, address?: string): Promise<FindPlaceResult> {
    return request<FindPlaceResult>('/api/v1/integrations/google/find-place', {
      method: 'POST',
      body: { name, address: address ?? '' },
    });
  },
};
